import { z } from 'zod'

import type { AgreementToAccept } from '@/features/site-management/license-agreements/license-agreement'
import type { ApiClient } from '@/shared/api/client'

import { activityTypes, type LaunchGrant } from '../launch-types'

/** The API's launches. */
export const launchesApi = '/launch/v1/launches'

/** The license agreements to accept before launching an activity under a grant. */
export function launchAgreementsApi(activityId: string, grant: LaunchGrant): string {
  const query = new URLSearchParams({ activityId, ...grantFields(grant) })
  return `${launchesApi}/license-agreements?${query.toString()}`
}

/** A launch's request body: the activity and the one grant it runs under. */
export function launchBody(activityId: string, grant: LaunchGrant): Readonly<Record<string, string>> {
  return { activityId, ...grantFields(grant) }
}

/** The grant's own id, as a license agreement acceptance names it. */
export function grantIdOf(grant: LaunchGrant): string {
  switch (grant.kind) {
    case 'enrollment':
      return grant.enrollmentId
    case 'subscription':
      return grant.subscriptionId
    case 'class':
      return grant.classId
  }
}

function grantFields(grant: LaunchGrant): Readonly<Record<string, string>> {
  switch (grant.kind) {
    case 'enrollment':
      return { enrollmentId: grant.enrollmentId }
    case 'subscription':
      return { subscriptionId: grant.subscriptionId }
    case 'class':
      return { classId: grant.classId }
  }
}

const launchSchema = z.object({
  id: z.string(),
  activityId: z.string().nullish(),
  activityType: z.enum(activityTypes),
  status: z.enum(['pending', 'inProgress', 'completed', 'ended', 'failed']),
  url: z.string().nullish(),
  labInstanceId: z.string().nullish(),
})

/** A launch as the launch flow hands it to the button that asked: where the browser goes, if anywhere. */
export type Launch = z.infer<typeof launchSchema>

export function parseLaunch(body: unknown): Launch {
  return launchSchema.parse(body)
}

/**
 * What asks for a launch that went through once more: its activity, grant and `Idempotency-Key`.
 * The same key starts nothing again; the API checks access again and answers with a fresh address.
 */
export interface LaunchRepeat {
  readonly activityId: string
  readonly grant: LaunchGrant
  readonly idempotencyKey: string
}

/** The same launch once more, with a fresh address; the API keeps a key for 24 hours. */
export async function repeatLaunch(client: ApiClient, repeat: LaunchRepeat): Promise<Launch> {
  return parseLaunch(await client.post(launchesApi, launchBody(repeat.activityId, repeat.grant), { idempotencyKey: repeat.idempotencyKey }))
}

/** A fresh link for the caller's own video launch, by its id, however long ago it was launched; nothing launches again. */
export async function renewLaunchLink(client: ApiClient, launchId: string): Promise<Launch> {
  return parseLaunch(await client.post(`${launchesApi}/${encodeURIComponent(launchId)}/link`))
}

/** Whether the value is a launch id as the API gives one, safe to keep and put in a path. */
export function isLaunchId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,100}$/.test(value)
}

/** Whether a launch's URL is one the browser may go to: http or https, never a script or data URL. */
export function isWebUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

/** Opens a blank new window cut off from this page, for an address to go into later; `null` when the pop-up was blocked. */
export function openBlankWindow(): Window | null {
  const tab = window.open('', '_blank')
  if (tab !== null) {
    tab.opener = null
  }
  return tab
}

/**
 * Opens the URL in a new window cut off from this page before it loads, so it can never reach
 * back here. Answers `false`, opening nothing, when the pop-up was blocked.
 */
export function openInNewWindow(url: string): boolean {
  const tab = openBlankWindow()
  if (tab === null) {
    return false
  }
  tab.location.href = url
  return true
}

const agreementsSchema = z.object({
  data: z.array(z.object({ id: z.string(), name: z.string(), text: z.string(), lastChangedOn: z.string() })),
})

/** The agreements to accept, in the order the access answer named them. */
export function parseAgreementsToAccept(body: unknown): readonly AgreementToAccept[] {
  return agreementsSchema.parse(body).data
}
